// Snippet extraction (ARCHITECTURE.md §11.5).
//
// The LLM needs to see actual code, but sending whole files wastes tokens. This
// extracts a few lines around each symbol — using the line numbers already in
// the IR (`SymbolInfo`) plus a configurable context window — so the prompt
// carries the minimum source needed to judge a relationship. Exported symbols
// are prioritized, and both the per-snippet size and the snippet count are
// bounded.

import type { SymbolInfo } from "../analyzer/types.js";
import {
  DEFAULT_SNIPPET_CONTEXT_LINES,
  type SourceSnippet,
} from "./types.js";

/** Default maximum number of snippets returned per file. */
export const DEFAULT_MAX_SNIPPETS = 5;

/**
 * Extract relevant code snippets from `content` around `symbols`.
 *
 * Exported symbols come first (they define the file's public surface and are
 * the most likely to participate in a cross-file relationship). When the file
 * has no symbols, a single header snippet (the first few lines) is returned so
 * the LLM still has minimal context. Lines are 1-based and clamped to the file.
 */
export function extractSnippets(
  filePath: string,
  content: string,
  symbols: SymbolInfo[],
  maxSnippets: number = DEFAULT_MAX_SNIPPETS,
): SourceSnippet[] {
  void filePath; // reserved for future per-file extraction strategies
  if (maxSnippets <= 0) return [];

  const lines = content.split("\n");
  const total = lines.length;
  if (total === 0 || (total === 1 && lines[0] === "")) return [];

  const window = DEFAULT_SNIPPET_CONTEXT_LINES;

  // No symbols: a header snippet still gives the LLM minimal context.
  if (symbols.length === 0) {
    const endLine = Math.min(total, window);
    return [
      {
        startLine: 1,
        endLine,
        content: lines.slice(0, endLine).join("\n"),
        context: "surrounding",
      },
    ];
  }

  // Exported symbols first, then by source order; cap the count.
  const ordered = [...symbols].sort((a, b) => {
    if (a.exported !== b.exported) return a.exported ? -1 : 1;
    return a.line - b.line;
  });

  const snippets: SourceSnippet[] = [];
  const seen = new Set<string>();
  for (const symbol of ordered) {
    if (snippets.length >= maxSnippets) break;
    // Clamp the symbol line into the valid 1-based range.
    const anchor = Math.min(Math.max(symbol.line, 1), total);
    const startLine = Math.max(1, anchor - 1);
    const endLine = Math.min(total, anchor + window);
    const key = `${startLine} ${endLine}`;
    if (seen.has(key)) continue;
    seen.add(key);
    snippets.push({
      startLine,
      endLine,
      content: lines.slice(startLine - 1, endLine).join("\n"),
      context: "signature",
    });
  }

  return snippets;
}
